export type AuthProfileHealthStatus = "ok" | "expiring" | "expired" | "missing" | "static";

export type AuthProfileHealth = {
  profileId: string;
  provider: string;
  type: "oauth" | "token" | "api_key";
  status: AuthProfileHealthStatus;
  expiresAt?: number;
  remainingMs?: number;
  source: string;
  label: string;
};

export type AuthProviderHealth = {
  provider: string;
  status: AuthProfileHealthStatus;
  expiresAt?: number;
  remainingMs?: number;
  profiles: AuthProfileHealth[];
};

export type RuntimeTokenHealthStatus = "ok" | "expiring" | "expired" | "missing" | "invalid";
export type RuntimeSubsystemHealthStatus = "ok" | "degraded" | "blocked";

export type RuntimeBackgroundTokenStatus = {
  status: RuntimeTokenHealthStatus;
  source?: "env" | "file" | "generated";
  verified: boolean;
  expiresAtMs?: number;
  remainingMs?: number;
  reason?: string;
  subject?: string;
  orgId?: string;
  path?: string;
};

export type RuntimeBackgroundContextStatus = {
  available: boolean;
  source: "env" | "device-token" | "none";
  reason?: string;
};

export type RuntimeReplyAuthStatus = {
  tenantAuthRequired: boolean;
  backgroundFallbackAllowed: boolean;
  summary: string;
};

export type RuntimeSubsystemHealth = {
  id: string;
  name: string;
  status: RuntimeSubsystemHealthStatus;
  reason: string;
};

export type RuntimeAuthOverview = {
  checkedAtMs: number;
  databaseConfigured: boolean;
  backgroundToken: RuntimeBackgroundTokenStatus;
  backgroundContext: RuntimeBackgroundContextStatus;
  replyRuntime: RuntimeReplyAuthStatus;
  subsystems: RuntimeSubsystemHealth[];
};

export type AuthIssueSeverity = "info" | "warning" | "error";
export type AuthIssueScope = "session" | "provider" | "runtime";

export type AuthStatusAction =
  | {
      id: string;
      kind: "navigate";
      label: string;
      href: string;
    }
  | {
      id: string;
      kind: "copy-command";
      label: string;
      command: string;
    }
  | {
      id: string;
      kind: "copy-text";
      label: string;
      text: string;
    }
  | {
      id: string;
      kind: "refresh-status";
      label: string;
    };

export type AuthStatusIssue = {
  id: string;
  scope: AuthIssueScope;
  severity: AuthIssueSeverity;
  summary: string;
  detail?: string;
  actions: AuthStatusAction[];
};

export type MeAuthStatus = {
  checkedAtMs: number;
  session: {
    authenticated: true;
    userId: string;
    orgId: string;
    orgRole: string;
    authMethod: string;
    expiresAtMs: number;
    isPlatformAdmin: boolean;
  };
  providerAuth: AuthProviderHealth[];
  runtime:
    | (RuntimeAuthOverview & {
        degraded: boolean;
      })
    | null;
};

export function formatRemainingMs(remainingMs?: number): string {
  if (remainingMs === undefined || Number.isNaN(remainingMs)) {
    return "unknown";
  }
  if (remainingMs <= 0) {
    return "expired";
  }
  const minutes = Math.max(1, Math.round(remainingMs / 60_000));
  if (minutes < 60) {
    return `${minutes}m`;
  }
  const hours = Math.round(minutes / 60);
  if (hours < 48) {
    return `${hours}h`;
  }
  const days = Math.round(hours / 24);
  return `${days}d`;
}

export function formatTimestampMs(value?: number): string {
  if (typeof value !== "number" || !Number.isFinite(value) || value <= 0) {
    return "unknown";
  }
  return new Date(value).toLocaleString();
}

export function authStatusClasses(
  status: AuthProfileHealthStatus | RuntimeTokenHealthStatus | RuntimeSubsystemHealthStatus,
): string {
  if (status === "ok" || status === "static") {
    return "border-emerald-200 bg-emerald-500/10 text-emerald-700";
  }
  if (status === "expiring" || status === "degraded") {
    return "border-amber-200 bg-amber-500/10 text-amber-700";
  }
  if (
    status === "expired" ||
    status === "missing" ||
    status === "invalid" ||
    status === "blocked"
  ) {
    return "border-red-200 bg-red-500/10 text-red-700";
  }
  return "border-muted bg-muted/40 text-foreground";
}

export function authIssueSeverityClasses(severity: AuthIssueSeverity): string {
  if (severity === "error") {
    return "border-red-200 bg-red-500/10 text-red-700";
  }
  if (severity === "warning") {
    return "border-amber-200 bg-amber-500/10 text-amber-700";
  }
  return "border-blue-200 bg-blue-500/10 text-blue-700";
}

export function summarizeProviderIssues(providers: AuthProviderHealth[]): string[] {
  return providers
    .filter(
      (provider) =>
        provider.status === "expired" ||
        provider.status === "expiring" ||
        provider.status === "missing",
    )
    .map((provider) => {
      if (provider.status === "expired") {
        return `${provider.provider} expired`;
      }
      if (provider.status === "missing") {
        return `${provider.provider} missing`;
      }
      return `${provider.provider} expires in ${formatRemainingMs(provider.remainingMs)}`;
    });
}

export function summarizeRuntimeIssues(runtime: RuntimeAuthOverview): string[] {
  const issues: string[] = [];
  if (runtime.backgroundToken.status === "expired") {
    issues.push("Gateway background token expired");
  } else if (runtime.backgroundToken.status === "expiring") {
    issues.push(
      `Gateway background token expires in ${formatRemainingMs(runtime.backgroundToken.remainingMs)}`,
    );
  } else if (runtime.backgroundToken.status === "missing") {
    issues.push("Gateway background token missing");
  } else if (runtime.backgroundToken.status === "invalid") {
    issues.push("Gateway background token invalid");
  }
  for (const subsystem of runtime.subsystems) {
    if (subsystem.status !== "ok") {
      issues.push(subsystem.name);
    }
  }
  return issues;
}

const SESSION_WARN_AFTER_MS = 15 * 60_000;
const DEFAULT_COHERENCE_TOKEN_PATH = "~/.claude/coherence_device_token.json";
const SETTINGS_AUTH_HEALTH_HREF = "/settings#auth-runtime-health";
const ADMIN_RUNTIME_AUTH_HEALTH_HREF = "/admin/workbench?surface=overview#runtime-auth-health";

function issueSortValue(issue: AuthStatusIssue): number {
  const severityOrder: Record<AuthIssueSeverity, number> = {
    error: 0,
    warning: 1,
    info: 2,
  };
  const scopeOrder: Record<AuthIssueScope, number> = {
    runtime: 0,
    session: 1,
    provider: 2,
  };
  return severityOrder[issue.severity] * 10 + scopeOrder[issue.scope];
}

function buildSettingsAction(viewerIsAdmin: boolean): AuthStatusAction {
  return {
    id: viewerIsAdmin ? "open-admin-runtime-health" : "open-settings",
    kind: "navigate",
    label: viewerIsAdmin ? "Open admin health" : "Open settings",
    href: viewerIsAdmin ? ADMIN_RUNTIME_AUTH_HEALTH_HREF : SETTINGS_AUTH_HEALTH_HREF,
  };
}

function buildRefreshAction(): AuthStatusAction {
  return {
    id: "refresh-status",
    kind: "refresh-status",
    label: "Recheck",
  };
}

function buildTokenLookupHint(): string {
  return [
    "Token lookup order:",
    "- COHERENCE_DEVICE_TOKEN",
    "- COHERENCE_DEVICE_TOKEN_PATH",
    `- ${DEFAULT_COHERENCE_TOKEN_PATH}`,
    "- ~/.coherence/device_token",
    "- ~/.synapsedx/kb/device_token",
  ].join("\n");
}

function buildBackgroundTokenRecoveryText(runtime: RuntimeAuthOverview): string {
  const path = runtime.backgroundToken.path;
  const configuredPath = path?.trim() || DEFAULT_COHERENCE_TOKEN_PATH;
  const lines = [
    "Gateway runtime auth recovery",
    "",
    "1. On the gateway host, run:",
    "   coherence auth device",
    "",
  ];

  if (runtime.backgroundToken.source === "env") {
    lines.push("2. Update COHERENCE_DEVICE_TOKEN with the refreshed access_token.");
    lines.push(
      "3. Restart or redeploy the gateway if that env value is injected only at process start.",
    );
  } else {
    lines.push(`2. Ensure the refreshed token file is available at: ${configuredPath}`);
    if (path && path !== DEFAULT_COHERENCE_TOKEN_PATH) {
      lines.push(
        `3. If coherence wrote ${DEFAULT_COHERENCE_TOKEN_PATH}, copy that refreshed token file to the configured path above.`,
      );
      lines.push("4. Recheck Runtime Auth Health.");
    } else {
      lines.push("3. Recheck Runtime Auth Health.");
    }
  }

  lines.push("", buildTokenLookupHint());
  return lines.join("\n");
}

function buildBackgroundContextRecoveryText(runtime: RuntimeAuthOverview): string {
  return [
    "Gateway background context recovery",
    "",
    runtime.backgroundContext.reason ?? "No background execution context is available.",
    "",
    "For multi-tenant runtime replies, keep tenant-bound auth dynamic.",
    "Only true background jobs should rely on service-level device auth.",
    "",
    buildTokenLookupHint(),
  ].join("\n");
}

function resolveProviderRepairCommand(provider: string): string {
  switch (provider) {
    case "openai-codex":
      return "command models auth login --provider openai-codex";
    case "qwen-portal":
      return "command models auth login --provider qwen-portal";
    case "anthropic":
      return "command models auth setup-token";
    default:
      return "command configure";
  }
}

function buildProviderRecoveryText(provider: AuthProviderHealth): string {
  const command = resolveProviderRepairCommand(provider.provider);
  return [
    `Provider auth recovery: ${provider.provider}`,
    "",
    `1. On this machine, run: ${command}`,
    "2. Re-authenticate or rotate the affected credentials.",
    "3. Recheck Auth & Runtime Health.",
  ].join("\n");
}

function describeBackgroundTokenDetail(runtime: RuntimeAuthOverview): string | undefined {
  const reason = runtime.backgroundToken.reason?.trim();
  const path = runtime.backgroundToken.path?.trim();
  const location = path
    ? `Current token path: ${path}.`
    : runtime.backgroundToken.source === "env"
      ? "The gateway is reading the background token from COHERENCE_DEVICE_TOKEN."
      : runtime.backgroundToken.source === "generated"
        ? "The gateway is minting short-lived runtime device tokens from COMMAND_USER_ID and COMMAND_ORG_ID."
        : undefined;
  return [reason, location].filter(Boolean).join(" ") || undefined;
}

export function resolveRuntimeAuthIssues(
  runtime: RuntimeAuthOverview,
  options?: {
    viewerIsAdmin?: boolean;
  },
): AuthStatusIssue[] {
  const viewerIsAdmin = options?.viewerIsAdmin === true;
  const issues: AuthStatusIssue[] = [];
  const settingsAction = buildSettingsAction(viewerIsAdmin);

  if (runtime.backgroundToken.status === "expired") {
    issues.push({
      id: "runtime-background-token-expired",
      scope: "runtime",
      severity: "error",
      summary: "Gateway background token expired",
      detail:
        describeBackgroundTokenDetail(runtime) ||
        "Background runtime jobs that still require device auth need a fresh token.",
      actions: [
        {
          id: "copy-background-token-command",
          kind: "copy-command",
          label: "Copy command",
          command: "coherence auth device",
        },
        settingsAction,
        {
          id: "copy-background-token-recovery",
          kind: "copy-text",
          label: "Copy recovery steps",
          text: buildBackgroundTokenRecoveryText(runtime),
        },
        buildRefreshAction(),
      ],
    });
  } else if (runtime.backgroundToken.status === "expiring") {
    issues.push({
      id: "runtime-background-token-expiring",
      scope: "runtime",
      severity: "warning",
      summary: `Gateway background token expires in ${formatRemainingMs(runtime.backgroundToken.remainingMs)}`,
      detail:
        describeBackgroundTokenDetail(runtime) ||
        "Renew the gateway token now to avoid runtime degradation.",
      actions: [
        {
          id: "copy-background-token-command",
          kind: "copy-command",
          label: "Copy command",
          command: "coherence auth device",
        },
        settingsAction,
        {
          id: "copy-background-token-recovery",
          kind: "copy-text",
          label: "Copy recovery steps",
          text: buildBackgroundTokenRecoveryText(runtime),
        },
        buildRefreshAction(),
      ],
    });
  } else if (runtime.backgroundToken.status === "missing") {
    issues.push({
      id: "runtime-background-token-missing",
      scope: "runtime",
      severity: "error",
      summary: "Gateway background token missing",
      detail:
        describeBackgroundTokenDetail(runtime) ||
        "No Coherence device token is available for background runtime jobs.",
      actions: [
        {
          id: "copy-background-token-command",
          kind: "copy-command",
          label: "Copy command",
          command: "coherence auth device",
        },
        settingsAction,
        {
          id: "copy-background-token-recovery",
          kind: "copy-text",
          label: "Copy recovery steps",
          text: buildBackgroundTokenRecoveryText(runtime),
        },
        buildRefreshAction(),
      ],
    });
  } else if (runtime.backgroundToken.status === "invalid") {
    issues.push({
      id: "runtime-background-token-invalid",
      scope: "runtime",
      severity: "error",
      summary: "Gateway background token invalid",
      detail:
        describeBackgroundTokenDetail(runtime) ||
        "The configured Coherence device token could not be verified.",
      actions: [
        {
          id: "copy-background-token-command",
          kind: "copy-command",
          label: "Copy command",
          command: "coherence auth device",
        },
        settingsAction,
        {
          id: "copy-background-token-recovery",
          kind: "copy-text",
          label: "Copy recovery steps",
          text: buildBackgroundTokenRecoveryText(runtime),
        },
        buildRefreshAction(),
      ],
    });
  } else if (!runtime.backgroundContext.available) {
    issues.push({
      id: "runtime-background-context-unavailable",
      scope: "runtime",
      severity: "warning",
      summary: "Background execution context unavailable",
      detail:
        runtime.backgroundContext.reason ||
        "No background execution context is currently available.",
      actions: [
        settingsAction,
        {
          id: "copy-background-context-recovery",
          kind: "copy-text",
          label: "Copy recovery steps",
          text: buildBackgroundContextRecoveryText(runtime),
        },
        buildRefreshAction(),
      ],
    });
  }

  for (const subsystem of runtime.subsystems) {
    if (subsystem.status === "ok") {
      continue;
    }
    issues.push({
      id: `runtime-subsystem-${subsystem.id}`,
      scope: "runtime",
      severity: subsystem.status === "blocked" ? "error" : "warning",
      summary: `${subsystem.name} ${subsystem.status}`,
      detail: subsystem.reason,
      actions: [settingsAction, buildRefreshAction()],
    });
  }

  return issues.toSorted((a, b) => issueSortValue(a) - issueSortValue(b));
}

export function resolveMeAuthIssues(
  status: MeAuthStatus,
  options?: {
    viewerIsAdmin?: boolean;
  },
): AuthStatusIssue[] {
  const issues = status.runtime ? resolveRuntimeAuthIssues(status.runtime, options) : [];
  const sessionRemainingMs = status.session.expiresAtMs - Date.now();

  // Clerk JWTs are short-lived (~60s) and auto-refreshed by the SDK.
  // Only warn for non-Clerk auth methods (e.g. device tokens with longer TTLs).
  const isAutoRefreshedSession = status.session.authMethod === "clerk";

  if (
    !isAutoRefreshedSession &&
    sessionRemainingMs > 0 &&
    sessionRemainingMs <= SESSION_WARN_AFTER_MS
  ) {
    issues.push({
      id: "session-expiring",
      scope: "session",
      severity: "warning",
      summary: `Session expires in ${formatRemainingMs(sessionRemainingMs)}`,
      detail: "Sign in again now to avoid interrupting durable chat operations.",
      actions: [
        {
          id: "sign-in-again",
          kind: "navigate",
          label: "Sign in again",
          href: "/sign-in",
        },
        buildRefreshAction(),
      ],
    });
  }

  for (const provider of status.providerAuth) {
    if (
      provider.status !== "expired" &&
      provider.status !== "expiring" &&
      provider.status !== "missing"
    ) {
      continue;
    }
    issues.push({
      id: `provider-${provider.provider}-${provider.status}`,
      scope: "provider",
      severity:
        provider.status === "expired" || provider.status === "missing" ? "error" : "warning",
      summary:
        provider.status === "expired"
          ? `${provider.provider} auth expired`
          : provider.status === "missing"
            ? `${provider.provider} auth missing`
            : `${provider.provider} auth expires in ${formatRemainingMs(provider.remainingMs)}`,
      detail:
        provider.status === "expiring"
          ? "Renew this provider auth before requests start failing."
          : "Requests using this provider may fail until you re-authenticate.",
      actions: [
        {
          id: `provider-${provider.provider}-command`,
          kind: "copy-command",
          label: "Copy re-auth command",
          command: resolveProviderRepairCommand(provider.provider),
        },
        {
          id: `provider-${provider.provider}-settings`,
          kind: "navigate",
          label: "Open settings",
          href: SETTINGS_AUTH_HEALTH_HREF,
        },
        {
          id: `provider-${provider.provider}-steps`,
          kind: "copy-text",
          label: "Copy recovery steps",
          text: buildProviderRecoveryText(provider),
        },
        buildRefreshAction(),
      ],
    });
  }

  return issues.toSorted((a, b) => issueSortValue(a) - issueSortValue(b));
}
